import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LiveKitRoom, 
  RoomAudioRenderer, 
  useParticipants,
  useLocalParticipant,
  useTracks,
  TrackToggle
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track } from 'livekit-client';
import './CouncilChamber.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LIVEKIT_URL = process.env.REACT_APP_LIVEKIT_URL;

// Council Chamber background image from environment
const CHAMBER_BG = process.env.REACT_APP_COUNCIL_CHAMBER_IMAGE_URL || "https://customer-assets.emergentagent.com/job_page-builder-225/artifacts/04dv6ado_Council%20Chamber.png";

// Voice Room UI Component
function VoiceRoomUI({ onLeave, user }) {
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const audioTracks = useTracks([Track.Source.Microphone]);
  
  // Get speaking state for each participant
  const getSpeakingState = (participant) => {
    const track = audioTracks.find(t => t.participant.identity === participant.identity);
    return track?.publication?.track?.isMuted === false;
  };
  
  // Parse participant metadata to get avatar
  const getParticipantAvatar = (participant) => {
    try {
      if (participant.metadata) {
        const metadata = JSON.parse(participant.metadata);
        return metadata.avatar_url || null;
      }
    } catch (e) {
      console.error('Error parsing participant metadata:', e);
    }
    return null;
  };
  
  return (
    <div className="voice-room-container" data-testid="voice-room">
      {/* Participants Grid */}
      <div className="voice-participants">
        <h4>In Voice Channel ({participants.length})</h4>
        <div className="participants-grid">
          {participants.map((participant) => {
            const isSpeaking = getSpeakingState(participant);
            const isLocal = participant.identity === localParticipant?.identity;
            const isMuted = participant.isMicrophoneEnabled === false;
            const avatarUrl = getParticipantAvatar(participant);
            
            return (
              <div 
                key={participant.identity}
                className={`participant-card ${isSpeaking ? 'speaking' : ''} ${isMuted ? 'muted' : ''}`}
              >
                <div className="participant-avatar">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={participant.name || 'User'} />
                  ) : (
                    <span className="avatar-letter">{participant.name?.charAt(0) || '?'}</span>
                  )}
                </div>
                <div className="participant-info">
                  <span className="participant-name">
                    {participant.name || 'Unknown'}
                    {isLocal && <span className="you-badge">(You)</span>}
                  </span>
                  <span className="participant-status">
                    {isMuted ? '🔇 Muted' : '🎤 Speaking'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Controls */}
      <div className="voice-controls">
        <TrackToggle 
          source={Track.Source.Microphone}
          className="voice-control-btn mute-btn"
        >
          {({ enabled }) => (
            <span>{enabled ? '🎤 Mute' : '🔇 Unmute'}</span>
          )}
        </TrackToggle>
        
        <button 
          className="voice-control-btn leave-btn"
          onClick={onLeave}
          data-testid="leave-voice-btn"
        >
          📞 Leave Channel
        </button>
      </div>
    </div>
  );
}

function CouncilChamber({ user, token }) {
  const navigate = useNavigate();
  
  // Access control - only these roles can enter
  const allowedRoles = ['Founder', 'Aurel', 'Archivist', 'Steward'];
  const hasAccess = user && allowedRoles.includes(user.role);
  const isFounder = user?.role === 'Founder';
  
  // Tab state
  const [activeTab, setActiveTab] = useState('discussions');
  
  // Discussions state
  const [discussions, setDiscussions] = useState([]);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [discussionReplies, setDiscussionReplies] = useState([]);
  const [newDiscussionTitle, setNewDiscussionTitle] = useState('');
  const [newDiscussionContent, setNewDiscussionContent] = useState('');
  const [newReply, setNewReply] = useState('');
  const [showNewDiscussion, setShowNewDiscussion] = useState(false);
  const [discussionCategory, setDiscussionCategory] = useState('general');
  
  // Settings state (Founder only)
  const [showSettings, setShowSettings] = useState(false);
  const [categories, setCategories] = useState([
    { id: 'general', name: 'General', color: '#6b7280' },
    { id: 'governance', name: 'Governance', color: '#d4b978' },
    { id: 'strategy', name: 'Strategy', color: '#3b82f6' },
    { id: 'community', name: 'Community', color: '#4ade80' },
    { id: 'treasury', name: 'Treasury', color: '#fbbf24' }
  ]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#6b7280');
  
  // History state (Settings)
  const [settingsTab, setSettingsTab] = useState('categories'); // 'categories', 'discussion-history', 'proposal-history'
  const [discussionHistory, setDiscussionHistory] = useState([]);
  const [proposalHistory, setProposalHistory] = useState([]);
  
  // Proposals state
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [newProposalTitle, setNewProposalTitle] = useState('');
  const [newProposalDescription, setNewProposalDescription] = useState('');
  const [newProposalOptions, setNewProposalOptions] = useState(['Yes', 'No']);
  const [showNewProposal, setShowNewProposal] = useState(false);
  
  // Loading/error state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Voice Council state
  const [voiceToken, setVoiceToken] = useState(null);
  const [voiceConnecting, setVoiceConnecting] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  const [voiceRoomName] = useState('council-chamber');
  
  const repliesEndRef = useRef(null);

  // Fetch discussions
  const fetchDiscussions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/council/discussions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDiscussions(data.discussions || []);
      }
    } catch (err) {
      console.error('Error fetching discussions:', err);
    }
  };

  // Fetch proposals
  const fetchProposals = async () => {
    try {
      const response = await fetch(`${API_URL}/api/council/proposals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProposals(data.proposals || []);
      }
    } catch (err) {
      console.error('Error fetching proposals:', err);
    }
  };

  // Fetch discussion replies
  const fetchReplies = async (discussionId) => {
    try {
      const response = await fetch(`${API_URL}/api/council/discussions/${discussionId}/replies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDiscussionReplies(data.replies || []);
      }
    } catch (err) {
      console.error('Error fetching replies:', err);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (hasAccess) {
      const loadData = async () => {
        setLoading(true);
        await Promise.all([fetchDiscussions(), fetchProposals(), fetchCategories()]);
        setLoading(false);
      };
      loadData();
    }
  }, [token, hasAccess]);

  // Fetch categories from server
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/council/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.categories && data.categories.length > 0) {
          setCategories(data.categories);
        }
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Save categories to server (Founder only)
  const saveCategories = async (newCategories) => {
    try {
      const response = await fetch(`${API_URL}/api/council/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ categories: newCategories })
      });
      if (response.ok) {
        setSuccess('Categories saved');
        setTimeout(() => setSuccess(null), 2000);
      }
    } catch (err) {
      setError('Failed to save categories');
    }
  };

  // Add a new category
  const addCategory = () => {
    if (!newCategoryName.trim()) {
      setError('Category name is required');
      return;
    }
    
    const categoryId = newCategoryName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    
    if (categories.find(c => c.id === categoryId)) {
      setError('Category already exists');
      return;
    }
    
    const newCategories = [...categories, {
      id: categoryId,
      name: newCategoryName.trim(),
      color: newCategoryColor
    }];
    
    setCategories(newCategories);
    setNewCategoryName('');
    setNewCategoryColor('#6b7280');
    saveCategories(newCategories);
    setError(null);
  };

  // Remove a category
  const removeCategory = (categoryId) => {
    if (categoryId === 'general') {
      setError('Cannot remove the general category');
      return;
    }
    
    const newCategories = categories.filter(c => c.id !== categoryId);
    setCategories(newCategories);
    saveCategories(newCategories);
  };

  // Fetch discussion history
  const fetchDiscussionHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/council/discussions/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDiscussionHistory(data.discussions || []);
      }
    } catch (err) {
      console.error('Error fetching discussion history:', err);
    }
  };

  // Fetch proposal history
  const fetchProposalHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/council/proposals/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProposalHistory(data.proposals || []);
      }
    } catch (err) {
      console.error('Error fetching proposal history:', err);
    }
  };

  // Delete discussion (soft delete)
  const deleteDiscussion = async (discussionId) => {
    if (!window.confirm('Move this discussion to history?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/council/discussions/${discussionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSuccess('Discussion moved to history');
        setTimeout(() => setSuccess(null), 2000);
        fetchDiscussions();
        if (selectedDiscussion?.id === discussionId) {
          setSelectedDiscussion(null);
        }
      }
    } catch (err) {
      setError('Failed to delete discussion');
    }
  };

  // Restore discussion from history
  const restoreDiscussion = async (discussionId) => {
    try {
      const response = await fetch(`${API_URL}/api/council/discussions/${discussionId}/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSuccess('Discussion restored');
        setTimeout(() => setSuccess(null), 2000);
        fetchDiscussionHistory();
        fetchDiscussions();
      }
    } catch (err) {
      setError('Failed to restore discussion');
    }
  };

  // Permanently delete discussion
  const permanentDeleteDiscussion = async (discussionId) => {
    if (!window.confirm('Permanently delete this discussion? This cannot be undone.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/council/discussions/${discussionId}/permanent`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSuccess('Discussion permanently deleted');
        setTimeout(() => setSuccess(null), 2000);
        fetchDiscussionHistory();
      }
    } catch (err) {
      setError('Failed to delete discussion');
    }
  };

  // Delete proposal (soft delete)
  const deleteProposal = async (proposalId) => {
    if (!window.confirm('Move this proposal to history?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/council/proposals/${proposalId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSuccess('Proposal moved to history');
        setTimeout(() => setSuccess(null), 2000);
        fetchProposals();
      }
    } catch (err) {
      setError('Failed to delete proposal');
    }
  };

  // Restore proposal from history
  const restoreProposal = async (proposalId) => {
    try {
      const response = await fetch(`${API_URL}/api/council/proposals/${proposalId}/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSuccess('Proposal restored');
        setTimeout(() => setSuccess(null), 2000);
        fetchProposalHistory();
        fetchProposals();
      }
    } catch (err) {
      setError('Failed to restore proposal');
    }
  };

  // Permanently delete proposal
  const permanentDeleteProposal = async (proposalId) => {
    if (!window.confirm('Permanently delete this proposal? This cannot be undone.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/council/proposals/${proposalId}/permanent`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSuccess('Proposal permanently deleted');
        setTimeout(() => setSuccess(null), 2000);
        fetchProposalHistory();
      }
    } catch (err) {
      setError('Failed to delete proposal');
    }
  };

  // Load history when settings tab changes
  useEffect(() => {
    if (showSettings && settingsTab === 'discussion-history') {
      fetchDiscussionHistory();
    } else if (showSettings && settingsTab === 'proposal-history') {
      fetchProposalHistory();
    }
  }, [showSettings, settingsTab]);

  // Scroll to bottom of replies
  useEffect(() => {
    if (repliesEndRef.current) {
      repliesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [discussionReplies]);

  // ==================== VOICE COUNCIL FUNCTIONS ====================
  
  // Join voice room
  const joinVoiceRoom = async () => {
    setVoiceConnecting(true);
    setVoiceError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/council/voice/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ room_name: voiceRoomName })
      });
      
      if (response.ok) {
        const data = await response.json();
        setVoiceToken(data.token);
      } else {
        const err = await response.json();
        setVoiceError(err.detail || 'Failed to join voice room');
      }
    } catch (err) {
      console.error('Error joining voice:', err);
      setVoiceError('Failed to connect to voice service');
    } finally {
      setVoiceConnecting(false);
    }
  };
  
  // Leave voice room
  const leaveVoiceRoom = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/council/voice/leave`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ room_name: voiceRoomName })
      });
    } catch (err) {
      console.error('Error leaving voice:', err);
    }
    setVoiceToken(null);
  }, [token, voiceRoomName]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (voiceToken) {
        leaveVoiceRoom();
      }
    };
  }, [voiceToken, leaveVoiceRoom]);

  // Create new discussion
  const createDiscussion = async () => {
    if (!newDiscussionTitle.trim() || !newDiscussionContent.trim()) {
      setError('Title and content are required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/council/discussions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newDiscussionTitle,
          content: newDiscussionContent,
          category: discussionCategory
        })
      });

      if (response.ok) {
        setSuccess('Discussion created');
        setNewDiscussionTitle('');
        setNewDiscussionContent('');
        setShowNewDiscussion(false);
        fetchDiscussions();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to create discussion');
      }
    } catch (err) {
      setError('Failed to create discussion');
    }
  };

  // Add reply to discussion
  const addReply = async () => {
    if (!newReply.trim() || !selectedDiscussion) return;

    try {
      const response = await fetch(`${API_URL}/api/council/discussions/${selectedDiscussion.id}/replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newReply })
      });

      if (response.ok) {
        setNewReply('');
        fetchReplies(selectedDiscussion.id);
        fetchDiscussions(); // Update reply count
      }
    } catch (err) {
      setError('Failed to add reply');
    }
  };

  // Pin/unpin discussion (Founders only)
  const togglePinDiscussion = async (discussionId) => {
    try {
      const response = await fetch(`${API_URL}/api/council/discussions/${discussionId}/pin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchDiscussions();
      }
    } catch (err) {
      setError('Failed to pin discussion');
    }
  };

  // Create new proposal
  const createProposal = async () => {
    if (!newProposalTitle.trim() || !newProposalDescription.trim()) {
      setError('Title and description are required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/council/proposals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newProposalTitle,
          description: newProposalDescription,
          options: newProposalOptions.filter(o => o.trim())
        })
      });

      if (response.ok) {
        setSuccess('Proposal created');
        setNewProposalTitle('');
        setNewProposalDescription('');
        setNewProposalOptions(['Yes', 'No']);
        setShowNewProposal(false);
        fetchProposals();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to create proposal');
      }
    } catch (err) {
      setError('Failed to create proposal');
    }
  };

  // Vote on proposal
  const voteOnProposal = async (proposalId, optionIndex) => {
    try {
      const response = await fetch(`${API_URL}/api/council/proposals/${proposalId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ option_index: optionIndex })
      });

      if (response.ok) {
        setSuccess('Vote recorded');
        fetchProposals();
        setTimeout(() => setSuccess(null), 2000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to vote');
      }
    } catch (err) {
      setError('Failed to vote');
    }
  };

  // Close proposal (Founders only)
  const closeProposal = async (proposalId) => {
    try {
      const response = await fetch(`${API_URL}/api/council/proposals/${proposalId}/close`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Proposal closed');
        fetchProposals();
        setTimeout(() => setSuccess(null), 2000);
      }
    } catch (err) {
      setError('Failed to close proposal');
    }
  };

  // Select a discussion and load its replies
  const openDiscussion = (discussion) => {
    setSelectedDiscussion(discussion);
    fetchReplies(discussion.id);
  };

  // Get category info
  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || { id: categoryId, name: categoryId, color: '#6b7280' };
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Access denied view
  if (!hasAccess) {
    return (
      <div className="council-chamber" style={{ backgroundImage: `url(${CHAMBER_BG})` }}>
        <div className="chamber-overlay"></div>
        <div className="access-denied">
          <h2>The Council Chamber</h2>
          <p>Only members of the Council may enter these halls.</p>
          <p className="required-ranks">Required ranks: Founder, Aurel, Archivist, or Steward</p>
          <button onClick={() => navigate('/')} className="return-btn">
            Return to Guild Hall
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="council-chamber" data-testid="council-chamber">
      {/* Background */}
      <div className="chamber-bg-container">
        <img src={CHAMBER_BG} alt="" className="chamber-bg" />
        <div className="chamber-overlay"></div>
      </div>

      {/* Header */}
      <header className="chamber-header">
        <button onClick={() => navigate('/')} className="back-btn" data-testid="back-to-guild">
          ← Guild Hall
        </button>
        <div className="chamber-title-section">
          <h1 className="chamber-title">The Council Chamber</h1>
          <div className="chamber-motto">Where Wisdom Guides Our Path</div>
        </div>
        {isFounder && (
          <button 
            className="chamber-settings-btn"
            onClick={() => setShowSettings(!showSettings)}
            data-testid="chamber-settings-btn"
          >
            ⚙️ Settings
          </button>
        )}
      </header>

      {/* Settings Panel (Founder only) */}
      {showSettings && isFounder && (
        <div className="chamber-settings-panel">
          <div className="settings-header">
            <h3>Council Settings</h3>
            <button className="close-settings" onClick={() => setShowSettings(false)}>×</button>
          </div>
          
          {/* Settings Tabs */}
          <div className="settings-tabs">
            <button 
              className={`settings-tab ${settingsTab === 'categories' ? 'active' : ''}`}
              onClick={() => setSettingsTab('categories')}
            >
              Categories
            </button>
            <button 
              className={`settings-tab ${settingsTab === 'discussion-history' ? 'active' : ''}`}
              onClick={() => setSettingsTab('discussion-history')}
            >
              Discussion History
            </button>
            <button 
              className={`settings-tab ${settingsTab === 'proposal-history' ? 'active' : ''}`}
              onClick={() => setSettingsTab('proposal-history')}
            >
              Proposal History
            </button>
          </div>
          
          {/* Categories Tab */}
          {settingsTab === 'categories' && (
            <div className="settings-section">
              <h4>Discussion Categories</h4>
              <p className="settings-hint">Add or remove topics for discussions</p>
              
              <div className="category-list">
                {categories.map(cat => (
                  <div key={cat.id} className="category-item">
                    <span className="category-color" style={{ backgroundColor: cat.color }}></span>
                    <span className="category-name">{cat.name}</span>
                    {cat.id !== 'general' && (
                      <button 
                        className="remove-category-btn"
                        onClick={() => removeCategory(cat.id)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="add-category-form">
                <input
                  type="text"
                  placeholder="New category name..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="category-name-input"
                />
                <input
                  type="color"
                  value={newCategoryColor}
                  onChange={(e) => setNewCategoryColor(e.target.value)}
                  className="category-color-input"
                />
                <button className="add-category-btn" onClick={addCategory}>
                  Add
                </button>
              </div>
            </div>
          )}

          {/* Discussion History Tab */}
          {settingsTab === 'discussion-history' && (
            <div className="settings-section">
              <h4>Discussion History</h4>
              <p className="settings-hint">Deleted discussions can be restored or permanently removed</p>
              
              <div className="history-list">
                {discussionHistory.length === 0 ? (
                  <p className="no-history">No deleted discussions</p>
                ) : (
                  discussionHistory.map(disc => (
                    <div key={disc.id} className="history-item">
                      <div className="history-info">
                        <span className="history-title">{disc.title}</span>
                        <span className="history-meta">
                          by {disc.author} • {disc.reply_count} replies • Deleted {formatDate(disc.deleted_at)}
                        </span>
                      </div>
                      <div className="history-actions">
                        <button 
                          className="restore-btn"
                          onClick={() => restoreDiscussion(disc.id)}
                        >
                          Restore
                        </button>
                        <button 
                          className="permanent-delete-btn"
                          onClick={() => permanentDeleteDiscussion(disc.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Proposal History Tab */}
          {settingsTab === 'proposal-history' && (
            <div className="settings-section">
              <h4>Proposal History</h4>
              <p className="settings-hint">Deleted proposals can be restored or permanently removed</p>
              
              <div className="history-list">
                {proposalHistory.length === 0 ? (
                  <p className="no-history">No deleted proposals</p>
                ) : (
                  proposalHistory.map(prop => (
                    <div key={prop.id} className="history-item">
                      <div className="history-info">
                        <span className="history-title">{prop.title}</span>
                        <span className="history-meta">
                          by {prop.author} • {prop.total_votes} votes • Deleted {formatDate(prop.deleted_at)}
                        </span>
                      </div>
                      <div className="history-actions">
                        <button 
                          className="restore-btn"
                          onClick={() => restoreProposal(prop.id)}
                        >
                          Restore
                        </button>
                        <button 
                          className="permanent-delete-btn"
                          onClick={() => permanentDeleteProposal(prop.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <main className="chamber-content">
        {/* Messages */}
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Tabs */}
        <div className="chamber-tabs">
          <button 
            className={`chamber-tab ${activeTab === 'discussions' ? 'active' : ''}`}
            onClick={() => { setActiveTab('discussions'); setSelectedDiscussion(null); }}
            data-testid="discussions-tab"
          >
            Discussions
          </button>
          <button 
            className={`chamber-tab ${activeTab === 'proposals' ? 'active' : ''}`}
            onClick={() => { setActiveTab('proposals'); setSelectedProposal(null); }}
            data-testid="proposals-tab"
          >
            Proposals & Votes
          </button>
          <button 
            className={`chamber-tab ${activeTab === 'voice' ? 'active' : ''}`}
            onClick={() => setActiveTab('voice')}
            data-testid="voice-tab"
          >
            Voice Council
          </button>
        </div>

        {/* Tab Content */}
        <div className="chamber-tab-content">
          {loading ? (
            <div className="chamber-loading">Entering the Chamber...</div>
          ) : (
            <>
              {/* Discussions Tab */}
              {activeTab === 'discussions' && (
                <div className="discussions-panel">
                  {!selectedDiscussion ? (
                    <>
                      {/* Discussion List Header */}
                      <div className="panel-header">
                        <h3>Council Discussions</h3>
                        <button 
                          className="new-btn"
                          onClick={() => setShowNewDiscussion(true)}
                          data-testid="new-discussion-btn"
                        >
                          + New Discussion
                        </button>
                      </div>

                      {/* New Discussion Form */}
                      {showNewDiscussion && (
                        <div className="new-discussion-form">
                          <input
                            type="text"
                            value={newDiscussionTitle}
                            onChange={(e) => setNewDiscussionTitle(e.target.value)}
                            placeholder="Discussion title..."
                            className="discussion-title-input"
                            data-testid="discussion-title-input"
                          />
                          <select 
                            value={discussionCategory}
                            onChange={(e) => setDiscussionCategory(e.target.value)}
                            className="category-select"
                          >
                            {categories.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                          </select>
                          <textarea
                            value={newDiscussionContent}
                            onChange={(e) => setNewDiscussionContent(e.target.value)}
                            placeholder="Share your thoughts with the Council..."
                            className="discussion-content-input"
                            rows={4}
                            data-testid="discussion-content-input"
                          />
                          <div className="form-actions">
                            <button onClick={createDiscussion} className="submit-btn">Create Discussion</button>
                            <button onClick={() => setShowNewDiscussion(false)} className="cancel-btn">Cancel</button>
                          </div>
                        </div>
                      )}

                      {/* Discussion List */}
                      <div className="discussions-list">
                        {discussions.length === 0 ? (
                          <div className="empty-state">No discussions yet. Start a new topic!</div>
                        ) : (
                          discussions.map((disc) => (
                            <div 
                              key={disc.id}
                              className={`discussion-card ${disc.is_pinned ? 'pinned' : ''}`}
                              onClick={() => openDiscussion(disc)}
                              data-testid={`discussion-${disc.id}`}
                            >
                              {disc.is_pinned && <span className="pin-badge">📌 Pinned</span>}
                              <div className="discussion-header">
                                <span className="category-badge" style={{ backgroundColor: getCategoryInfo(disc.category).color }}>
                                  {getCategoryInfo(disc.category).name}
                                </span>
                                <h4 className="discussion-title">{disc.title}</h4>
                              </div>
                              <p className="discussion-preview">{disc.content.substring(0, 150)}...</p>
                              <div className="discussion-meta">
                                <span className="author">
                                  <img src={disc.author_avatar || '/default-avatar.png'} alt="" className="author-avatar" />
                                  {disc.author}
                                </span>
                                <span className="date">{formatDate(disc.created_at)}</span>
                                <span className="replies-count">{disc.reply_count || 0} replies</span>
                              </div>
                              {isFounder && (
                                <div className="founder-actions">
                                  <button 
                                    className="pin-btn"
                                    onClick={(e) => { e.stopPropagation(); togglePinDiscussion(disc.id); }}
                                  >
                                    {disc.is_pinned ? 'Unpin' : 'Pin'}
                                  </button>
                                  <button 
                                    className="delete-btn"
                                    onClick={(e) => { e.stopPropagation(); deleteDiscussion(disc.id); }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  ) : (
                    /* Discussion Detail View */
                    <div className="discussion-detail">
                      <button className="back-link" onClick={() => setSelectedDiscussion(null)}>
                        ← Back to Discussions
                      </button>
                      
                      <div className="discussion-full">
                        <span className="category-badge" style={{ backgroundColor: getCategoryInfo(selectedDiscussion.category).color }}>
                          {getCategoryInfo(selectedDiscussion.category).name}
                        </span>
                        <h2>{selectedDiscussion.title}</h2>
                        <div className="discussion-author-info">
                          <img 
                            src={selectedDiscussion.author_avatar || '/default-avatar.png'} 
                            alt="" 
                            className="author-avatar-lg" 
                          />
                          <div>
                            <span className="author-name">{selectedDiscussion.author}</span>
                            <span className="author-role">{selectedDiscussion.author_role}</span>
                          </div>
                          <span className="post-date">{formatDate(selectedDiscussion.created_at)}</span>
                        </div>
                        <div className="discussion-body">{selectedDiscussion.content}</div>
                      </div>

                      {/* Replies */}
                      <div className="replies-section">
                        <h4>Replies ({discussionReplies.length})</h4>
                        <div className="replies-list">
                          {discussionReplies.map((reply) => (
                            <div key={reply.id} className="reply-card">
                              <div className="reply-header">
                                <img 
                                  src={reply.author_avatar || '/default-avatar.png'} 
                                  alt="" 
                                  className="reply-avatar" 
                                />
                                <span className="reply-author">{reply.author}</span>
                                <span className="reply-role">{reply.author_role}</span>
                                <span className="reply-date">{formatDate(reply.created_at)}</span>
                              </div>
                              <div className="reply-content">{reply.content}</div>
                            </div>
                          ))}
                          <div ref={repliesEndRef} />
                        </div>

                        {/* Reply Form */}
                        <div className="reply-form">
                          <textarea
                            value={newReply}
                            onChange={(e) => setNewReply(e.target.value)}
                            placeholder="Add your voice to the discussion..."
                            rows={3}
                            data-testid="reply-input"
                          />
                          <button onClick={addReply} className="reply-submit-btn" data-testid="submit-reply">
                            Send Reply
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Proposals Tab */}
              {activeTab === 'proposals' && (
                <div className="proposals-panel">
                  <div className="panel-header">
                    <h3>Council Proposals</h3>
                    <button 
                      className="new-btn"
                      onClick={() => setShowNewProposal(true)}
                      data-testid="new-proposal-btn"
                    >
                      + New Proposal
                    </button>
                  </div>

                  {/* New Proposal Form */}
                  {showNewProposal && (
                    <div className="new-proposal-form">
                      <input
                        type="text"
                        value={newProposalTitle}
                        onChange={(e) => setNewProposalTitle(e.target.value)}
                        placeholder="Proposal title..."
                        className="proposal-title-input"
                        data-testid="proposal-title-input"
                      />
                      <textarea
                        value={newProposalDescription}
                        onChange={(e) => setNewProposalDescription(e.target.value)}
                        placeholder="Describe your proposal..."
                        className="proposal-desc-input"
                        rows={4}
                        data-testid="proposal-desc-input"
                      />
                      <div className="options-editor">
                        <label>Voting Options:</label>
                        {newProposalOptions.map((opt, idx) => (
                          <div key={idx} className="option-row">
                            <input
                              type="text"
                              value={opt}
                              onChange={(e) => {
                                const updated = [...newProposalOptions];
                                updated[idx] = e.target.value;
                                setNewProposalOptions(updated);
                              }}
                              placeholder={`Option ${idx + 1}`}
                            />
                            {newProposalOptions.length > 2 && (
                              <button 
                                className="remove-option"
                                onClick={() => setNewProposalOptions(newProposalOptions.filter((_, i) => i !== idx))}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                        {newProposalOptions.length < 5 && (
                          <button 
                            className="add-option-btn"
                            onClick={() => setNewProposalOptions([...newProposalOptions, ''])}
                          >
                            + Add Option
                          </button>
                        )}
                      </div>
                      <div className="form-actions">
                        <button onClick={createProposal} className="submit-btn">Create Proposal</button>
                        <button onClick={() => setShowNewProposal(false)} className="cancel-btn">Cancel</button>
                      </div>
                    </div>
                  )}

                  {/* Proposals List */}
                  <div className="proposals-list">
                    {proposals.length === 0 ? (
                      <div className="empty-state">No proposals yet. Create one to start a vote!</div>
                    ) : (
                      proposals.map((proposal) => (
                        <div 
                          key={proposal.id}
                          className={`proposal-card ${proposal.status}`}
                          data-testid={`proposal-${proposal.id}`}
                        >
                          <div className="proposal-header">
                            <h4>{proposal.title}</h4>
                            <span className={`status-badge ${proposal.status}`}>
                              {proposal.status === 'active' ? 'Voting Open' : 'Closed'}
                            </span>
                          </div>
                          <p className="proposal-description">{proposal.description}</p>
                          <div className="proposal-meta">
                            <span>Proposed by {proposal.author}</span>
                            <span>{formatDate(proposal.created_at)}</span>
                            <span>{proposal.total_votes || 0} votes cast</span>
                          </div>

                          {/* Voting Options */}
                          <div className="voting-options">
                            {proposal.options.map((option, idx) => {
                              const voteCount = proposal.vote_counts?.[idx] || 0;
                              const totalVotes = proposal.total_votes || 0;
                              const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
                              const userVoted = proposal.user_vote === idx;

                              return (
                                <div key={idx} className={`vote-option ${userVoted ? 'voted' : ''}`}>
                                  <div className="option-info">
                                    <span className="option-text">{option}</span>
                                    <span className="option-stats">{voteCount} votes ({percentage}%)</span>
                                  </div>
                                  <div className="vote-bar">
                                    <div className="vote-fill" style={{ width: `${percentage}%` }}></div>
                                  </div>
                                  {proposal.status === 'active' && proposal.user_vote === null && (
                                    <button 
                                      className="vote-btn"
                                      onClick={() => voteOnProposal(proposal.id, idx)}
                                    >
                                      Vote
                                    </button>
                                  )}
                                  {userVoted && <span className="your-vote">Your vote</span>}
                                </div>
                              );
                            })}
                          </div>

                          {/* Close button for Founders */}
                          {isFounder && proposal.status === 'active' && (
                            <button 
                              className="close-proposal-btn"
                              onClick={() => closeProposal(proposal.id)}
                            >
                              Close Voting
                            </button>
                          )}
                          
                          {/* Delete button for Founders */}
                          {isFounder && (
                            <button 
                              className="delete-proposal-btn"
                              onClick={() => deleteProposal(proposal.id)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Voice Council Tab */}
              {activeTab === 'voice' && (
                <div className="voice-panel">
                  <div className="voice-header">
                    <h3>Voice Council</h3>
                    <p className="voice-subtitle">Gather your voices in the Chamber</p>
                  </div>

                  <div className="voice-content">
                    {voiceError && (
                      <div className="voice-error">
                        <span>{voiceError}</span>
                        <button onClick={() => setVoiceError(null)}>×</button>
                      </div>
                    )}
                    
                    {!voiceToken ? (
                      // Not connected - show join button
                      <div className="voice-join-section">
                        <div className="voice-icon">🎙️</div>
                        <h4>Voice Council Chamber</h4>
                        <p>Join the live voice channel to speak with other Council members in real-time.</p>
                        <button 
                          className="voice-join-btn"
                          onClick={joinVoiceRoom}
                          disabled={voiceConnecting}
                          data-testid="join-voice-btn"
                        >
                          {voiceConnecting ? 'Connecting...' : 'Join Voice Channel'}
                        </button>
                        <div className="voice-features">
                          <span>🔇 Mute controls</span>
                          <span>👥 See who's speaking</span>
                          <span>🎧 High-quality audio</span>
                        </div>
                      </div>
                    ) : (
                      // Connected - show LiveKit room
                      <LiveKitRoom
                        serverUrl={LIVEKIT_URL}
                        token={voiceToken}
                        connect={true}
                        audio={true}
                        video={false}
                        onDisconnected={leaveVoiceRoom}
                        data-lk-theme="default"
                        className="livekit-room"
                      >
                        <VoiceRoomUI 
                          onLeave={leaveVoiceRoom}
                          user={user}
                        />
                        <RoomAudioRenderer />
                      </LiveKitRoom>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Arcolia Values - pillars from the image */}
      <div className="chamber-pillars">
        <div className="pillar left">
          <span className="pillar-text">HONOUR</span>
          <span className="pillar-text">WISDOM</span>
          <span className="pillar-text">UNITY</span>
        </div>
        <div className="pillar right">
          <span className="pillar-text">BUILDING</span>
          <span className="pillar-text">A LEGACY</span>
          <span className="pillar-text">ETERNAL</span>
        </div>
      </div>
    </div>
  );
}

export default CouncilChamber;
